import type { CharacterShape } from "./stroke-shape.js";
import type { HandwritingIndex } from "./handwriting-lookup.js";
import { type StrokeComparison, compareStrokes } from "./stroke-comparison.js";
import { lookUpHandwriting } from "./handwriting-lookup.js";

/**
 * Whether some ink was a go at the character asked for, or at another one.
 *
 * Two questions a marked handwriting card asks after the grader has given its
 * rating, and neither moves the rating. The first decides whether the card
 * colours the character's strokes by what the reader made of each. The second
 * decides whether it can say "it looks like you wrote 王 instead of 玉" on a
 * card whose stroke count was wrong, where the ranking the grader reads is
 * never asked.
 *
 * `docs/fuxi.md` under "Which strokes were right" has both measured.
 */

/**
 * The share of the character's strokes, right and in order, that makes the ink
 * an attempt at it.
 *
 * Strokes are simple shapes and any character shares some with any other. Ink
 * of an unrelated character of the same count gets half of the character's
 * strokes right at the median, and colouring half a character green for it
 * would be praise for a horizontal that happened to be in the right place. Two
 * thirds lets through 1.3% of unrelated characters, and 98.3% of the
 * character's own ink with a stroke left out.
 */
export const attemptShare = 2 / 3;

/**
 * Whether the reader was writing this character, by what they made of it.
 *
 * @param comparison - What the ink made of each of the character's strokes.
 * @returns Whether enough of them were right, and in order.
 */
export function isAttempt(comparison: StrokeComparison): boolean {
  const { marks } = comparison;
  const right = marks.filter((mark) => mark === "right").length;

  return marks.length > 0 && right / marks.length >= attemptShare;
}

/**
 * The character the reader wrote in full instead, where the ink is one.
 *
 * Asked of ink with the wrong stroke count, which the grader marks without
 * ranking. The nearest character with exactly as many strokes as were drawn is
 * the candidate, and it is named only where the ink gets every one of its
 * strokes right, in order, with none left over.
 *
 * **Never of ink that was an attempt at the card's own character.** A
 * character missing one stroke very often is another character with one fewer
 * strokes, and naming it on the strength of that alone was wrong for one
 * dropped stroke in ten: 切 without a stroke came back as 兀. The card's own
 * strokes, coloured, are the better answer there.
 *
 * @param properties - The ink, the characters to rank it against, and the one
 * the card asked for.
 * @returns The character, or nothing.
 */
export function writtenInstead(properties: {
  readonly shape: CharacterShape;
  readonly index: HandwritingIndex;
  readonly expected: string;
}): string | undefined {
  const { shape, index, expected } = properties;
  const pool = index.filter(
    (one) => one.character !== expected && one.shape.length === shape.length,
  );
  const first = lookUpHandwriting({ index: pool, shape, limit: 1 })[0];
  const entry = pool.find((one) => one.character === first?.character);

  if (entry === undefined) {
    return undefined;
  }

  const { marks, extra } = compareStrokes(shape, entry.shape);

  return extra === 0 && marks.every((mark) => mark === "right")
    ? entry.character
    : undefined;
}

/** What the strokes say about one handwriting answer, for its reading. */
export interface StrokeFindings {
  /** Every stroke of the character, compared, whatever the ink was. */
  readonly compared: StrokeComparison;

  /**
   * What a reading that is not right shows of it.
   *
   * The marks where the ink was an attempt at the character. Otherwise, on ink
   * with the wrong stroke count, the character written in full instead, where
   * there is one.
   */
  readonly shown: {
    readonly strokes?: StrokeComparison;
    readonly looksLike?: string;
  };
}

/**
 * Compare the ink with the character asked for, and say what to show of it.
 *
 * @param properties - The ink, the character's own strokes, the characters to
 * rank the ink against, and the character asked for.
 * @returns The comparison, and what a reading shows of it.
 */
export function strokeFindings(properties: {
  readonly shape: CharacterShape;
  readonly stored: CharacterShape;
  readonly index: HandwritingIndex;
  readonly expected: string;
}): StrokeFindings {
  const { shape, stored } = properties;
  const compared = compareStrokes(shape, stored);

  if (isAttempt(compared)) {
    return { compared, shown: { strokes: compared } };
  }

  const instead =
    shape.length === stored.length ? undefined : writtenInstead(properties);

  return {
    compared,
    shown: instead === undefined ? {} : { looksLike: instead },
  };
}
